/**
 * WhatDoYouSee — interactive reveal experience.
 * Visitor sees the image, types their own interpretation, then "Reveal The Truth"
 * unfolds the title, quote, meaning, story, and Buy Print CTA.
 *
 * Props:
 *   art         — full artwork record (title, main_image, short_description, story, quote, slug, ...)
 *   onReveal    — optional callback fired the moment the curtain drops
 *   compact     — if true, drops some chrome (used inside the standalone gallery)
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Eye } from "lucide-react";

export default function WhatDoYouSee({ art, onReveal, compact = false }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) { toast.error("Tell us what you see first."); return; }
    setBusy(true);
    try {
      await http.post(`/artworks/${art.id}/interpretations`, {
        text,
        name: user?.name || name || undefined,
        email: user?.email || email || undefined,
      });
      setRevealed(true);
      if (onReveal) onReveal();
      // Smooth scroll the reveal into view after the curtain drops
      setTimeout(() => {
        document.getElementById(`reveal-${art.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className={`${compact ? "" : "bg-[#050505] border-y border-white/5 py-12 md:py-20"}`} data-testid="what-do-you-see">
      <div className={`${compact ? "" : "max-w-3xl mx-auto px-5"}`}>
        {!compact && (
          <div className="text-center mb-8">
            <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] inline-flex items-center gap-2">
              <Eye size={13}/> WHAT DO YOU SEE?
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F4F1EA] mt-4 leading-[1.05]">
              Before we tell you what this piece means to us,<br className="hidden md:inline"/>
              <span className="text-[#C9A055]">what does it say to you?</span>
            </h2>
          </div>
        )}

        {/* Artwork image — always visible */}
        <div className="border border-white/10 bg-black overflow-hidden mb-6">
          <img
            src={resolveImage(art.main_image)}
            alt=""
            aria-label="An artwork inviting your own interpretation"
            className="w-full h-auto object-contain max-h-[70vh]"
            data-testid="wdys-image"
          />
        </div>

        {/* Interpretation form OR reveal */}
        {!revealed ? (
          <div className="space-y-4" data-testid="wdys-form">
            <textarea
              rows={5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type what this says to you. A feeling, a memory, a single word — there's no wrong answer."
              className="w-full bg-[#0a0a0a] border border-white/15 px-4 py-3 text-[#F4F1EA] font-body text-[15px] leading-relaxed focus:border-[#C9A055] focus:outline-none resize-y"
              data-testid="wdys-text"
            />
            {!user && (
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  type="text" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Your first name (optional)"
                  className="w-full bg-[#0a0a0a] border border-white/15 px-4 py-2.5 text-[#F4F1EA] font-body text-[14px] focus:border-[#C9A055] focus:outline-none"
                  data-testid="wdys-name"
                />
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email (optional — never shared)"
                  className="w-full bg-[#0a0a0a] border border-white/15 px-4 py-2.5 text-[#F4F1EA] font-body text-[14px] focus:border-[#C9A055] focus:outline-none"
                  data-testid="wdys-email"
                />
              </div>
            )}
            <button
              onClick={submit}
              disabled={busy}
              className="w-full bg-[#C9A055] text-black hover:opacity-90 font-ui text-[11px] tracking-[0.32em] uppercase py-4 inline-flex items-center justify-center gap-2 disabled:opacity-40 transition-opacity"
              data-testid="wdys-reveal-btn"
            >
              <Sparkles size={14}/> {busy ? "Saving…" : "Reveal The Truth"}
            </button>
            <p className="font-body text-[11px] text-[#6a6660] italic text-center">
              Your interpretation is saved privately for the artist.
            </p>
          </div>
        ) : (
          <div id={`reveal-${art.id}`} className="space-y-6 animate-[fadeIn_700ms_ease-out]" data-testid="wdys-revealed">
            <div className="text-center border-t border-[#C9A055]/30 pt-8">
              <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">THE TRUTH</div>
              <h3 className="font-display text-4xl md:text-5xl text-[#F4F1EA] mt-3 uppercase">{art.title}</h3>
            </div>

            {art.quote && (
              <blockquote className="border-l-2 border-[#C9A055] pl-5 italic font-script text-2xl md:text-3xl text-[#C9A055] leading-snug">
                "{art.quote}"
              </blockquote>
            )}

            {art.short_description && (
              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">THE MEANING</div>
                <p className="font-body text-[16px] text-[#F4F1EA] leading-relaxed">{art.short_description}</p>
              </div>
            )}

            {art.story && (
              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">THE FULL STORY</div>
                <div className="prose prose-invert prose-p:text-[#B5B0A7] prose-p:leading-relaxed max-w-none font-body" dangerouslySetInnerHTML={{ __html: art.story }}/>
              </div>
            )}

            <div className="border-t border-white/10 pt-6 flex flex-wrap gap-3">
              <Link to={`/artwork/${art.slug}#buy`} className="btn-gold inline-flex items-center gap-2" data-testid="wdys-buy-btn">
                Buy The Print <ArrowRight size={14}/>
              </Link>
              <Link to={`/artwork/${art.slug}#story`} className="btn-outline" data-testid="wdys-read-btn">Read More</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
